import { CommissionStatsPeriod } from "@/interfaces/CommissionStats";
import { useGetMyCommissionStatsQuery } from "@/services/commissions/stats";
import {
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faEye,
  faEyeSlash,
  faMoneyBill,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { DateTime } from "luxon";
import { FC, ReactNode, useState } from "react";
import { Card, Col, Collapse, Placeholder, Row } from "react-bootstrap";

const HIDDEN_KEY = "commissionStatsHidden";

const formatAmount = (n: number) =>
  n.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const MyCommissionStats: FC = () => {
  const [expanded, setExpanded] = useState(false);
  const [period, setPeriod] = useState<CommissionStatsPeriod>("month");
  const [hidden, setHidden] = useState<boolean>(
    () => localStorage.getItem(HIDDEN_KEY) !== "false"
  );

  const { data, isFetching } = useGetMyCommissionStatsQuery(period);

  // Affiché uniquement pour ceux qui ont réellement des commissions
  // (participation AE/SDR sur au moins une formation).
  if (!data?.hasCommissions) return null;

  const periodLabel =
    period === "month"
      ? DateTime.now().setLocale("fr").toFormat("LLLL yyyy")
      : `${DateTime.now()
          .startOf("week")
          .setLocale("fr")
          .toFormat("dd LLL")} – ${DateTime.now()
          .endOf("week")
          .setLocale("fr")
          .toFormat("dd LLL")}`;

  const toggleHidden = () =>
    setHidden((prev) => {
      const next = !prev;
      localStorage.setItem(HIDDEN_KEY, next ? "true" : "false");
      return next;
    });

  const renderValue = (value?: number): ReactNode => {
    if (hidden) return "••••• €";
    if (isFetching || value === undefined) {
      return (
        <Placeholder as="span" animation="glow">
          <Placeholder xs={6} />
        </Placeholder>
      );
    }
    return `${formatAmount(value)} €`;
  };

  const tile = (
    label: string,
    icon: typeof faMoneyBill,
    color: "warning" | "success",
    value?: number
  ) => (
    <Col xs={6}>
      <div className={`rounded-3 bg-soft-${color} p-3 h-100`}>
        <div className="d-flex align-items-center mb-2">
          <FontAwesomeIcon icon={icon} className={`text-${color} me-2`} />
          <span className="fs--1 fw-semi-bold text-700">{label}</span>
        </div>
        <h4 className="mb-0 text-900">{renderValue(value)}</h4>
      </div>
    </Col>
  );

  return (
    <Card className="mb-3">
      <Card.Header
        className="cursor-pointer d-flex align-items-center justify-content-between"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <h6 className="mb-0">Mes commissions</h6>
        <FontAwesomeIcon
          icon={expanded ? faChevronUp : faChevronDown}
          className="text-600"
        />
      </Card.Header>
      <Collapse in={expanded}>
        <div>
          <Card.Body>
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
              <span className="text-700 fw-semi-bold text-capitalize">
                {periodLabel}
              </span>
              <div className="d-flex align-items-center gap-2">
                <div className="d-inline-flex bg-200 rounded-pill p-1">
                {(["week", "month"] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPeriod(value)}
                    className={classNames(
                      "btn btn-sm rounded-pill border-0 px-3 py-1 fs--1 shadow-none",
                      {
                        "bg-white text-primary fw-semi-bold shadow-sm":
                          period === value,
                        "bg-transparent text-600": period !== value,
                      }
                    )}
                  >
                    {value === "week" ? "Semaine" : "Mois"}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={toggleHidden}
                className="btn btn-sm text-600 border-0 shadow-none px-2"
                title={hidden ? "Afficher les montants" : "Masquer les montants"}
              >
                <FontAwesomeIcon icon={hidden ? faEyeSlash : faEye} />
              </button>
              </div>
            </div>
            <Row className="g-3">
              {tile("À payer", faMoneyBill, "warning", data?.toPay)}
              {tile("Déjà payé", faCircleCheck, "success", data?.paid)}
            </Row>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

export default MyCommissionStats;
